export type Direction = 'left' | 'right' | 'up' | 'down'
export type GroupKind = 'row' | 'col' | 'grid'

export interface Box {
  left: number
  top: number
  width: number
  height: number
}

/** Does moving `direction` from `group` leave it, rather than move inside it? */
export function exitsGroup(kind: GroupKind, direction: Direction, atEdge: boolean) {
  if (kind === 'grid') return atEdge
  const along = kind === 'row'
    ? direction === 'left' || direction === 'right'
    : direction === 'up' || direction === 'down'
  return !along || atEdge
}

/** Step within a linear group. Returns the new index, or -1 when already at the edge. */
export function stepInGroup(kind: GroupKind, direction: Direction, index: number, count: number, columns = 1) {
  if (count <= 0) return -1
  if (kind === 'grid') {
    const delta = direction === 'left' ? -1
      : direction === 'right' ? 1
        : direction === 'up' ? -columns : columns
    const next = index + delta
    if (next < 0 || next >= count) return -1
    // horizontal moves must stay on the same visual row
    if ((direction === 'left' || direction === 'right') && Math.floor(next / columns) !== Math.floor(index / columns)) return -1
    return next
  }
  const forward = kind === 'row' ? direction === 'right' : direction === 'down'
  const backward = kind === 'row' ? direction === 'left' : direction === 'up'
  if (!forward && !backward) return -1
  const next = index + (forward ? 1 : -1)
  return next < 0 || next >= count ? -1 : next
}

/**
 * Step through a grid that has holes in it. `occupied` is the visual grid, one
 * entry per cell, false where nothing focusable sits — a keypad's blank corners,
 * for example. Stepping over a hole continues in the same direction so a key
 * lines up with the one drawn above it rather than with its position in the
 * list of focusable elements.
 */
export function stepInGrid(occupied: boolean[], from: number, direction: Direction, columns: number) {
  if (columns <= 0 || from < 0 || from >= occupied.length) return -1
  const delta = direction === 'left' ? -1
    : direction === 'right' ? 1
      : direction === 'up' ? -columns : columns
  const row = Math.floor(from / columns)
  let next = from + delta
  while (next >= 0 && next < occupied.length) {
    // horizontal moves must stay on the same visual row
    if ((direction === 'left' || direction === 'right') && Math.floor(next / columns) !== row) return -1
    if (occupied[next]) return next
    next += delta
  }
  return -1
}

function centre(box: Box) {
  return { x: box.left + box.width / 2, y: box.top + box.height / 2 }
}

/**
 * Pick the best candidate in a direction. Distance along the axis of travel
 * dominates; drift across it is penalised heavily so focus lands where the eye
 * expects rather than wherever happens to be nearest in raw pixels.
 */
export function pickInDirection(origin: Box, candidates: Box[], direction: Direction) {
  const from = centre(origin)
  let best = -1
  let bestScore = Infinity

  candidates.forEach((box, index) => {
    const to = centre(box)
    const dx = to.x - from.x
    const dy = to.y - from.y
    const moved =
      (direction === 'left' && dx < -4) ||
      (direction === 'right' && dx > 4) ||
      (direction === 'up' && dy < -4) ||
      (direction === 'down' && dy > 4)
    if (!moved) return

    const horizontal = direction === 'left' || direction === 'right'
    const along = Math.abs(horizontal ? dx : dy)
    const across = Math.abs(horizontal ? dy : dx)
    // overlap on the cross axis means the target is genuinely "in line"
    const overlap = horizontal
      ? Math.min(origin.top + origin.height, box.top + box.height) - Math.max(origin.top, box.top)
      : Math.min(origin.left + origin.width, box.left + box.width) - Math.max(origin.left, box.left)
    const score = along + across * (overlap > 0 ? 0.4 : 3)
    if (score < bestScore) {
      bestScore = score
      best = index
    }
  })

  return best
}

/** Clamp a remembered index into a group that may have changed length. */
export function rememberedIndex(remembered: number | undefined, count: number) {
  if (!count) return -1
  if (remembered === undefined) return 0
  return Math.max(0, Math.min(count - 1, remembered))
}
